import React, { useContext, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";
import Header from "../components/Header";
import { Content } from "../components/Elements";
import { loadContractorAssessments } from "../store/assessment/actions";
import {
  getContractorAssessments,
  getContractorAssessmentsLoading,
} from "../store/assessment/selectors";
import moment from "moment";
import { device } from "../components/Devices";
import Loader from "../components/Loader";
import { getDisplayName, getRiskClassName } from "../components/ResultScreen";
import { ColorContext } from "../components/Bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/free-solid-svg-icons";

const Wrapper = styled.div`
  background-color: #fff;
  margin: 12px;
  padding: 24px;
  min-height: 420px;

  @media ${device.laptop} {
    width: 1080px;
    padding: 48px;
    margin: auto;
    margin-top: 48px;
    margin-bottom: 48px;
  }
`;

const PageHeader = styled.div`
  border-bottom: 1px solid var(--app-border);
  padding-bottom: 24px;
  margin-bottom: 30px;

  h1 {
    color: var(--app-ink);
    font-size: 38px;
    line-height: 1.1;
    margin: 0;
    text-align: left;
  }

  p {
    color: #52636c;
    font-size: 18px;
    line-height: 1.5;
    margin: 10px 0 0;
  }
`;

const CompanySection = styled.section`
  margin-top: 30px;

  &:first-of-type {
    margin-top: 0;
  }
`;

const CompanyHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;

  h2 {
    color: var(--app-ink);
    font-size: 24px;
    margin: 0;
  }

  span {
    color: #62727b;
    font-size: 15px;
    font-weight: 700;
  }
`;

const AssessmentList = styled.div`
  border: 1px solid var(--app-border);
  border-radius: 6px;
  overflow: hidden;
`;

const AssessmentRow = styled.button`
  appearance: none;
  background: #fff;
  border: 0;
  border-bottom: 1px solid var(--app-border);
  color: var(--app-ink);
  cursor: pointer;
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  padding: 20px;
  text-align: left;
  width: 100%;
  transition: background-color 140ms ease-in, border-color 140ms ease-in;

  &:last-child {
    border-bottom: 0;
  }

  &:hover,
  &:focus {
    background-color: #f7fbfb;
    outline: none;
  }

  @media ${device.laptop} {
    align-items: center;
    grid-template-columns: 1fr 1fr 1fr 1.25fr auto 34px;
    padding: 20px 24px;
  }
`;

const Detail = styled.div`
  span {
    color: #62727b;
    display: block;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0;
    margin-bottom: 5px;
    text-transform: uppercase;
  }

  div {
    color: var(--app-ink);
    display: block;
    font-size: 17px;
    font-weight: 500;
    line-height: 1.3;
  }
`;

const ResultPill = styled.div`
  border-radius: 4px;
  font-size: 15px;
  font-weight: 700;
  min-width: 130px;
  padding: 9px 13px;
  text-align: center;

  &.pass {
    background-color: #d7ffdb;
    color: #2f6f18;
  }

  &.undetermined {
    background-color: #fff0ca;
    color: #7a5600;
  }

  &.fail {
    background-color: #ffccd7;
    color: #881e21;
  }
`;

const RowArrow = styled.span`
  align-items: center;
  border: 1px solid ${(props) => props.$mainColor};
  border-radius: 50%;
  color: ${(props) => props.$mainColor};
  display: flex;
  font-size: 13px;
  height: 32px;
  justify-content: center;
  justify-self: start;
  line-height: 1;
  width: 32px;

  ${AssessmentRow}:hover &,
  ${AssessmentRow}:focus & {
    background-color: ${(props) => props.$mainColor};
    border-color: ${(props) => props.$mainColor};
    color: #fff;
  }

  @media ${device.laptop} {
    justify-self: end;
  }
`;

const EmptyState = styled.div`
  border: 1px solid var(--app-border);
  border-radius: 6px;
  padding: 30px;
  text-align: center;

  h2 {
    color: var(--app-ink);
    font-size: 24px;
    margin: 0 0 8px;
  }

  p {
    color: #62727b;
    font-size: 17px;
    margin: 0;
  }
`;

function ContractorList({ history }) {
  const dispatch = useDispatch();
  const mainColor = useContext(ColorContext);
  const loading = useSelector(getContractorAssessmentsLoading);
  const engagements = useSelector(getContractorAssessments);
  const totalAssessments = (engagements || []).reduce(
    (total, engagement) =>
      total +
      (engagement.engagements || []).filter(
        (row) => row.assessment && row.assessment.public_id
      ).length,
    0
  );

  const handleRowClicked = (row) => {
    if (row.assessment && row.assessment.public_id) {
      history.push(`/contractor-assessment/result/${row.assessment.public_id}`);
    }
  };
  const formatDate = (date) => (date ? moment(date).format("DD MMM YYYY") : "Not set");
  const getEndClient = (row) => row.end_client || "Not set";

  useEffect(() => {
    dispatch(loadContractorAssessments());
  }, []);

  return (
    <div>
      <Header showMenu />
      <Content>
        <Wrapper>
          <PageHeader>
            <h1>My Assessments</h1>
            <p>
              Your completed IR35 assessments are ready to review whenever you
              need them.
            </p>
          </PageHeader>
          {(loading || !engagements) && <Loader text="Loading, please wait" />}
          {!loading && engagements && totalAssessments === 0 && (
            <EmptyState>
              <h2>No completed assessments yet</h2>
              <p>Completed assessments will appear here.</p>
            </EmptyState>
          )}
          {!loading &&
            engagements &&
            engagements.map((engagement) => {
              const rows = (engagement.engagements || []).filter(
                (row) => row.assessment && row.assessment.public_id
              );

              if (!rows.length) {
                return null;
              }

              return (
                <CompanySection key={engagement.company.id}>
                  <CompanyHeader>
                    <h2>{engagement.company.name}</h2>
                    <span>
                      {rows.length} {rows.length === 1 ? "assessment" : "assessments"}
                    </span>
                  </CompanyHeader>
                  <AssessmentList>
                    {rows.map((row) => {
                      const resultClass = getRiskClassName(row.assessment.result);
                      const resultDisplay = getDisplayName(
                        row.assessment.result,
                        row.validation ? "SHORT" : "FULL"
                      );

                      return (
                        <AssessmentRow
                          key={row.id}
                          type="button"
                          onClick={() => handleRowClicked(row)}
                        >
                          <Detail>
                            <span>Submitted</span>
                            <div>
                              {formatDate(row.assessment.datetime)}
                            </div>
                          </Detail>
                          <Detail>
                            <span>Start</span>
                            <div>{formatDate(row.start_date)}</div>
                          </Detail>
                          <Detail>
                            <span>End</span>
                            <div>{formatDate(row.completion_date)}</div>
                          </Detail>
                          <Detail>
                            <span>End client</span>
                            <div>{getEndClient(row)}</div>
                          </Detail>
                          <ResultPill className={resultClass}>
                            {resultDisplay}
                          </ResultPill>
                          <RowArrow $mainColor={mainColor} aria-hidden="true">
                            <FontAwesomeIcon icon={faChevronRight} />
                          </RowArrow>
                        </AssessmentRow>
                      );
                    })}
                  </AssessmentList>
                </CompanySection>
              );
            })}
        </Wrapper>
      </Content>
    </div>
  );
}

export default ContractorList;
